// Backend PageGuard clamps `size` to 200 regardless of what's requested, so a single page can
// silently drop rows beyond the 200th -- real factories commonly carry >200 buyer orders. Page
// through (capped) instead of asking for one big page. Same cap CommercialPartyAutocomplete's
// original fetchAllPartiesOfType used before it was extracted here.
export const REF_PAGE_SIZE = 200;
export const MAX_REF_PAGES = 10;

interface PageLike<T> {
    content: T[];
    totalPages: number;
}

/**
 * Pages through a paginated list endpoint via `fetchPage(page, size)`, accumulating `content`
 * across pages. Stops at the reported `totalPages`, on a short (final) page, or after
 * `maxPages` pages -- whichever comes first. Shared by every "fetch all of this small master
 * data up front" picker: `CommercialPartyAutocomplete`, and the EXP form / export LC / buyer
 * order feeds behind `RefAutocomplete` on `PackingListForm`/`BillOfLadingForm`.
 */
export const fetchAllPages = async <T>(
    fetchPage: (page: number, size: number) => Promise<PageLike<T>>,
    size: number = REF_PAGE_SIZE,
    maxPages: number = MAX_REF_PAGES,
): Promise<T[]> => {
    const all: T[] = [];
    for (let page = 0; page < maxPages; page += 1) {
        const result = await fetchPage(page, size);
        all.push(...result.content);
        if (page + 1 >= result.totalPages || result.content.length < size) break;
    }
    return all;
};
